// Settings → Security → Overview: "Signed in now" (DESIGN-security.md, ideas 1 and 2). Admins only.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { securityApi } from '../../lib/api';
import { activeAgo } from '../../lib/securityWords';
import Flag from './Flag';
import SecurityCredit from './SecurityCredit';

export default function SignedInNow() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [askEveryone, setAskEveryone] = useState(false);

  const load = useCallback(() => {
    securityApi.sessions()
      .then((r) => { setData(r.data); setFailed(false); })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const signOut = async (s) => {
    setBusy(s.id);
    try {
      await securityApi.signOutSession(s.id);
      toast.success(`${s.name} is signed out on ${s.device}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign that session out');
    } finally {
      setBusy(null);
    }
  };

  const everyone = async () => {
    setAskEveryone(false);
    setBusy('everyone');
    try {
      await securityApi.signOutEveryone();
      toast.success('Everyone else has been signed out');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign everyone out');
    } finally {
      setBusy(null);
    }
  };

  if (failed) {
    return <div className="card-hive p-6"><p className="text-sm text-muted-foreground">Couldn't load who's signed in. Please refresh the page.</p></div>;
  }
  if (!data) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  const sessions = data.sessions || [];

  return (
    <div className="card-hive p-6" data-testid="security-signed-in-now">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h3 className="font-semibold">Signed in now</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Everyone signed in to your company, most recently active first. Signing somebody out
            takes effect on their next click.
          </p>
        </div>
        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-500/10 flex-shrink-0"
                disabled={busy === 'everyone'} onClick={() => setAskEveryone(true)} data-testid="sign-out-everyone">
          {busy === 'everyone' && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          Sign everyone out
        </Button>
      </div>

      <div className="mt-3">
        {sessions.length === 0 && <p className="text-sm text-muted-foreground py-4">Nobody is signed in right now.</p>}
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-3 py-3 border-t border-border first:border-t-0" data-testid="signed-in-row">
            <div className="w-8 h-8 rounded-full bg-secondary text-xs font-bold flex items-center justify-center flex-shrink-0">{s.initials}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium flex flex-wrap items-center gap-1.5">
                {s.name}
                {s.this_device && <span className="text-[11px] rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">This device</span>}
                {s.new_device && <span className="text-[11px] rounded-full px-2 py-0.5 bg-blue-500/15 text-blue-700 dark:text-blue-300">New device</span>}
                {s.support && <span className="text-[11px] rounded-full px-2 py-0.5 bg-purple-500/15 text-purple-700 dark:text-purple-300">HolidayHive support</span>}
              </p>
              <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                <span>{s.device}</span>
                {s.country && <><span>·</span><Flag country={s.country} /></>}
                {s.ip_address && <><span>·</span><span>IP {s.ip_address}</span></>}
                <span>·</span><span>{activeAgo(s.last_active_at)}</span>
              </p>
            </div>
            {!s.this_device && (
              <Button variant="outline" size="sm" disabled={busy === s.id} onClick={() => signOut(s)}>
                {busy === s.id && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Sign out
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Archiving someone, or resetting their password, signs them out everywhere too. People
        signed in before this page existed appear here the next time they sign in.
      </p>
      <SecurityCredit credit={data.credit} />

      <AlertDialog open={askEveryone} onOpenChange={setAskEveryone}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign everyone out?</AlertDialogTitle>
            <AlertDialogDescription>
              Everybody at your company is signed out on every device, straight away. They'll need
              to sign in again. You stay signed in on this device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep them signed in</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={everyone}>Sign everyone out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
